import { ErrorMessage, TaAutocomplete, TaInput } from '@components/shared';
import { MuiPhone } from '@components/shared/PhoneInput/PhoneInput';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { StepNames } from '@features/auth/registration/registration.types';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import Button from '@mui/material/Button';
import { validatePhoneNumberByCountryCode } from '@utils/phoneValidation';
import { Dictionary, Nullable } from '@utils/util.types';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountries, useCountriesStore } from 'services/countries';
import { CreateUserPayload, useCreateUser, useUsersStore } from 'services/users';
import { useCheckUserCompletedRegistration } from 'services/users/usersHook';
import * as Yup from 'yup';

const flagCDN = 'https://flagcdn.com/w160/';

const initialValues: CreateUserForm = {
	first_name: '',
	last_name: '',
	phone: '',
	country: null,
};

export interface CreateUserForm {
	first_name: string;
	last_name: string;
	phone: string;
	country: Nullable<Dictionary>;
}

export function FarmerPersonalDetailsNew() {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const { countries } = useCountriesStore();
	const createUserHandler = useCreateUser();
	const { user } = useUsersStore();
	const { checkStatus, isLoading: checkStatusLoading } = useCheckUserCompletedRegistration();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const [isLoading, setIsLoading] = useState(false);
	const [phoneValidationError, setPhoneValidationError] = useState<string>('');

	const personalDetailsValidationSchema = Yup.object({
		first_name: Yup.string().required('fieldNameError'),
		last_name: Yup.string().required('fieldNameError'),
		phone: Yup.string()
			.required('fieldNameError')
			.test('phone-validation', t('phoneNumberMatchError'), function (value) {
				if (!value) return false;

				const validation = validatePhoneNumberByCountryCode(value, t);
				if (!validation.isValid) {
					return this.createError({ message: validation.errorMessage });
				}
				return true;
			}),
		country: Yup.object().required('fieldNameError'),
	});

	useCountries();
	// useUser();

	useEffect(() => {
		const checkRegistrationStatus = async () => {
			const status = await checkStatus();

			if (!status?.user_completed_registration)
				showSnackbar(t('continueRegistration'), t('additionalFields'), 'success');
		};

		if (!checkStatusLoading) {
			checkRegistrationStatus();
		}
	}, [checkStatus, checkStatusLoading, t, showSnackbar]);

	const formik = useFormik<CreateUserForm>({
		initialValues: initialValues,
		validationSchema: personalDetailsValidationSchema,
		onSubmit: async (values) => {
			try {
				setIsLoading(true);

				// Final validation before submission
				const phoneValidation = validatePhoneNumberByCountryCode(values.phone, t);
				if (!phoneValidation.isValid) {
					setPhoneValidationError(phoneValidation.errorMessage || t('phoneNumberMatchError'));
					setIsLoading(false);
					return;
				}

				const formattedValues = {
					...values,
					country: values.country?.id,
				};

				await createUserHandler(formattedValues as unknown as CreateUserPayload);
				navigateWithLanguage('/farmer/preferences', {
					state: { step: StepNames.PREFERENCES },
				});
			} catch (error) {
				console.error(error);
			} finally {
				setIsLoading(false);
			}
		},
	});
	const { setValues } = formik;

	// Handle phone number change with real-time validation
	const handlePhoneChange = (phone: string) => {
		formik.setFieldValue('phone', phone);
		setPhoneValidationError('');

		// Real-time validation based on phone number's country code
		if (phone) {
			const validation = validatePhoneNumberByCountryCode(phone);
			if (!validation.isValid) {
				setPhoneValidationError(validation.errorMessage || t('phoneNumberMatchError'));
			}
		}
	};

	// Handle country change and re-validate phone if it exists
	const handleCountryChange = (event: any, value: any) => {
		formik.setFieldValue('country', value);
		setPhoneValidationError('');

		// Re-validate phone number (validation is independent of country dropdown)
		if (formik.values.phone) {
			const validation = validatePhoneNumberByCountryCode(formik.values.phone);
			if (!validation.isValid) {
				setPhoneValidationError(validation.errorMessage || t('phoneNumberMatchError'));
			}
		}
	};

	useEffect(() => {
		if (user) {
			setValues({
				first_name: user.first_name,
				last_name: user.last_name,
				phone: user.phone,
				country: countries.find(
					(country) => country.id === user.country || country.name === user.country
				),
			});
		}
	}, [countries, setValues, user]);

	const renderCountryIcon = (country: any) => {
		if (country?.alpha_two) {
			return (
				<img
					src={`${flagCDN}${country?.alpha_two.toLowerCase()}.png`}
					alt={country?.alpha_two}
					className="ml-[10px]"
					style={{ width: 24 }}
				/>
			);
		} else {
			return (
				<PublicOutlinedIcon
					className={classNames('text-secondary', {
						'text-error': formik.touched.country && formik.errors.country,
					})}
				/>
			);
		}
	};

	return (
		<>
			<p className="text-base text-dark font-bold">{t('personalDetails')}</p>
			<form onSubmit={formik.handleSubmit} className="mt-[20px] flex flex-col gap-[20px] pb-5">
				<div>
					<TaInput
						icon={
							<PersonOutlineOutlinedIcon
								sx={{
									color:
										formik.touched.first_name && formik.errors.first_name ? '#FD3333' : '#7B809A',
								}}
							/>
						}
						id={'first_name'}
						label={t('firstName')}
						size={'small'}
						value={formik.values.first_name}
						onChange={formik.handleChange}
						error={formik.errors.first_name}
						touched={formik.touched.first_name}
						isRequired
					/>
					{formik.touched.first_name && formik.errors.first_name && (
						<ErrorMessage
							message={t(formik.errors.first_name || '', {
								fieldName: t('firstName'),
							})}
						/>
					)}
				</div>
				<div>
					<TaInput
						icon={
							<PersonOutlineOutlinedIcon
								sx={{
									color:
										formik.touched.last_name && formik.errors.last_name ? '#FD3333' : '#7B809A',
								}}
							/>
						}
						id={'last_name'}
						label={t('lastName')}
						size={'small'}
						value={formik.values.last_name}
						onChange={formik.handleChange}
						error={formik.errors.last_name}
						touched={formik.touched.last_name}
						isRequired
					/>
					{formik.touched.last_name && formik.errors.last_name && (
						<ErrorMessage
							message={t(formik.errors.last_name || '', {
								fieldName: t('lastName'),
							})}
						/>
					)}
				</div>
				<div>
					<MuiPhone
						className="w-full"
						name="phone"
						placeholder={t('phoneNumber')}
						value={formik.values.phone}
						onChange={handlePhoneChange}
						onBlur={formik.handleBlur}
						error={
							(formik.touched.phone && Boolean(formik.errors.phone)) ||
							Boolean(phoneValidationError)
						}
					/>
					{((formik.touched.phone && formik.errors.phone) || phoneValidationError) && (
						<ErrorMessage
							message={
								phoneValidationError ||
								t(formik.errors.phone || '', {
									fieldName: t('phoneNumber'),
								})
							}
						/>
					)}
				</div>
				<div>
					<TaAutocomplete
						required
						hasFlag
						icon={renderCountryIcon(formik.values.country)}
						options={countries}
						getOptionLabel={(option) => option.name}
						label={t('country')}
						value={formik.values.country as Dictionary}
						onChange={handleCountryChange}
						error={Boolean(formik.touched.country && formik.errors.country)}
					/>
					{formik.touched.country && formik.errors.country && (
						<ErrorMessage
							message={t(formik.errors.country as string, { fieldName: t('country') })}
						/>
					)}
				</div>
				<Button color="primary" variant="contained" fullWidth type="submit" loading={isLoading}>
					{t('next')}
					<KeyboardArrowRightIcon />
				</Button>
			</form>
		</>
	);
}
